import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ContactUs.css';

const ROLES = [
  'Security Executive (CISO, VP Security, etc.)',
  'Security Practitioner (Red/Blue/Purple Team, Analyst)',
  'AI/ML Engineer',
  'AI Application Developer',
  'IT/Cloud Architect',
  'Product Manager (AI/ML or Security)',
  'GRC / Compliance',
  'Researcher / Academic',
  'Investor / Advisor',
  'Other',
];

const sanitize = (str) => str.replace(/[<>"'&]/g, '');

const ContactUs = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    role: '',
    otherRole: '',
    topic: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const renderRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.enterprise && recaptchaRef.current && !recaptchaRef.current.hasChildNodes()) {
        window.grecaptcha.enterprise.render(recaptchaRef.current, {
          sitekey: '6Lcfz4ssAAAAAIhyh2nC-YLe_YbDc6nXf9EiG9HQ',
          action: 'contact_us',
          callback: (token) => {
            setRecaptchaToken(token);
            setError('');
          },
          'expired-callback': () => {
            setRecaptchaToken(null);
          },
        });
      }
    };
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      window.grecaptcha.enterprise.ready(renderRecaptcha);
    } else {
      const interval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval);
          window.grecaptcha.enterprise.ready(renderRecaptcha);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: sanitize(value) });
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');

    if (!form.firstName || !form.lastName || !form.email || !form.role) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA challenge.');
      return;
    }

    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'https://bltzbackend.vercel.app';
      const response = await fetch(`${apiBase}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recaptchaToken,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          organization: form.organization,
          role: form.role === 'Other' ? form.otherRole : form.role,
          topic: form.topic || null,
          message: form.message,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Contact submission error:', err.message);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [form, recaptchaToken]);

  return (
    <>
      <section className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <h1 className="hero-page-title">Contact Us</h1>
      </section>

      <section className="ct-content">
        <div className="ct-layout">
          <div className="ct-left">
            <h2 className="ct-heading">Get in Touch</h2>
            <p className="ct-description">
              Have questions about AI security, governance, or how Bltz can help your organization? Reach out and our team will get back to you promptly.
            </p>

            <p className="ct-topic-label">What can we help you with?</p>
            <div className="ct-info-cards">
              <div className={`ct-info-card${form.topic === 'Live Demo' ? ' ct-info-card-selected' : ''}`} onClick={() => setForm({ ...form, topic: form.topic === 'Live Demo' ? '' : 'Live Demo' })}>
                <h4>Live Demo</h4>
                <p>See Bltz in action. Get a live walkthrough of Runtime Shield, Purple Team, and AI Governance tailored to your organization.</p>
              </div>
              <div className={`ct-info-card${form.topic === 'AI Governance Consultation' ? ' ct-info-card-selected' : ''}`} onClick={() => setForm({ ...form, topic: form.topic === 'AI Governance Consultation' ? '' : 'AI Governance Consultation' })}>
                <h4>AI Governance Consultation</h4>
                <p>Get expert guidance on AI governance best practices, compliance frameworks, and building a responsible AI program for your organization.</p>
              </div>
              <div className={`ct-info-card${form.topic === 'Partnerships' ? ' ct-info-card-selected' : ''}`} onClick={() => setForm({ ...form, topic: form.topic === 'Partnerships' ? '' : 'Partnerships' })}>
                <h4>Partnerships</h4>
                <p>Interested in partnering with Bltz AI? We work with MSSPs, system integrators, and technology vendors.</p>
              </div>
            </div>
          </div>

          <div className="ct-right">
            {submitted ? (
              <div className="ct-success">
                <h3>Thank you!</h3>
                <p>We've received your message and will be in touch shortly.</p>
                <button className="ct-explore-btn" onClick={() => navigate('/')}>Continue Exploring Bltz Platform &rarr;</button>
              </div>
            ) : (
              <form className="ct-form" onSubmit={handleSubmit}>
                <div className="ct-form-row">
                  <div className="ct-field">
                    <label>First name*</label>
                    <input
                      type="text"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="ct-field">
                    <label>Last name*</label>
                    <input
                      type="text"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="ct-field">
                  <label>Business email*</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    maxLength={200}
                    autoComplete="email"
                  />
                </div>

                <div className="ct-field">
                  <label>Organization</label>
                  <input
                    type="text"
                    name="organization"
                    value={form.organization}
                    onChange={handleChange}
                    maxLength={200}
                    autoComplete="organization"
                  />
                </div>

                <div className="ct-field">
                  <label>Your role*</label>
                  <select name="role" value={form.role} onChange={handleChange} required>
                    <option value="">Select your role</option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                {form.role === 'Other' && (
                  <div className="ct-field">
                    <label>Please specify</label>
                    <input
                      type="text"
                      name="otherRole"
                      value={form.otherRole}
                      onChange={handleChange}
                      maxLength={100}
                      placeholder="Enter your role"
                    />
                  </div>
                )}

                <div className="ct-field">
                  <label>Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={2000}
                    placeholder="How can we help?"
                  />
                </div>

                <div ref={recaptchaRef} className="ct-recaptcha"></div>

                {error && <p className="ct-error">{error}</p>}

                <button
                  type="submit"
                  className="ct-submit-btn"
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>

                <p className="ct-privacy">
                  By submitting, you agree to our <span onClick={() => { navigate('/privacy'); window.scrollTo(0, 0); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactUs;
